import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Text,
  Title,
} from "@clipboard-health/ui-components";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useATeamStatus } from "@src/appV2/Rankings/useATeamStatus";
import { PROFILE_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { useShouldBeOnboardedToStripe } from "@src/appV2/Stripe/useShouldBeOnboardedToStripe";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useHistory } from "react-router-dom";

import { CLIPBOARD_SCORE_FULL_PATH } from "../../ClipboardScore/utils/paths";
import { isWorkerInviteRequired } from "../../ClipboardScore/utils/utils";
import { NOTIFICATIONS_PREFERENCE_PATH_FULL } from "../../NotificationPreferences/paths";
import { WORK_WITH_FRIENDS_FULL_PATH } from "../../ShiftDiscovery/paths";
import { useIsSurfaceShiftsEnabled } from "../../ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { PAYOUT_SETTINGS_FULL_PATH } from "../PayoutSettings/paths";
import { ACCOUNT_ROLES_FULL_PATH } from "../Roles/paths";
import { logAccountPageMenuItemTapped } from "../utils";

export function AccountsPageSettingsSection() {
  const worker = useDefinedWorker();
  const history = useHistory();

  const licenseReciprocityEnabled = useCbhFlag(CbhFeatureFlag.LICENSE_RECIPROCITY, {
    defaultValue: false,
  });
  const isPayoutSettingsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_PAYOUT_SETTINGS, {
    defaultValue: false,
  });
  const tiersConfig = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS, {
    defaultValue: { enabled: false },
  });

  const { isATeamOffExperimentEnabled } = useATeamStatus();

  const shouldBeOnboardedInPaymentService = useShouldBeOnboardedToStripe();

  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();
  const manageLicensesTitleText = isSurfaceShiftsEnabled
    ? "Manage Roles & Licenses"
    : "Manage Licenses & Qualifications";

  const isInviteRequired = isWorkerInviteRequired(worker);

  const showClipboardScorePage =
    !tiersConfig.enabled && !isInviteRequired && isATeamOffExperimentEnabled;

  const showRatingsPage = !tiersConfig.enabled && !isInviteRequired && !isATeamOffExperimentEnabled;

  return (
    <>
      <Title semibold component="h4">
        Settings
      </Title>
      <List>
        <ListItem divider>
          <ListItemButton
            onClick={() => {
              logAccountPageMenuItemTapped("Profile");
              history.push(PROFILE_FULL_PATH);
            }}
          >
            <ListItemIcon iconType="user" />
            <ListItemText primary="Personal Information" />
          </ListItemButton>
        </ListItem>

        {showClipboardScorePage && (
          <ListItem divider>
            <ListItemButton
              onClick={() => {
                logAccountPageMenuItemTapped("Clipboard Score");
                history.push(CLIPBOARD_SCORE_FULL_PATH);
              }}
            >
              <ListItemIcon iconType="trending-up" />
              <ListItemText primary="Clipboard Score" />
            </ListItemButton>
          </ListItem>
        )}
        {showRatingsPage && (
          <ListItem divider>
            <ListItemButton
              onClick={() => {
                logAccountPageMenuItemTapped("Account Status");
                history.push(DeprecatedGlobalAppV1Paths.RATINGS);
              }}
            >
              <ListItemIcon iconType="trending-up" />
              <ListItemText primary="Account Status" />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem divider>
          <ListItemButton
            onClick={() => {
              logAccountPageMenuItemTapped(manageLicensesTitleText);
              history.push(
                isSurfaceShiftsEnabled
                  ? ACCOUNT_ROLES_FULL_PATH
                  : DeprecatedGlobalAppV1Paths.LICENSE_MANAGER
              );
            }}
          >
            <ListItemIcon iconType="document" />
            <ListItemText primary={manageLicensesTitleText} />
          </ListItemButton>
        </ListItem>
        {licenseReciprocityEnabled && (
          <ListItem divider>
            <ListItemButton
              onClick={() => {
                logAccountPageMenuItemTapped("Get Licensed in Another State");
                history.push(DeprecatedGlobalAppV1Paths.LICENSE_RECIPROCITY);
              }}
            >
              <ListItemIcon iconType="document-search" />
              <ListItemText
                primary="Get Licensed in Another State"
                secondary={
                  <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                    Apply for licenses in other states through reciprocity
                  </Text>
                }
              />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem divider>
          <ListItemButton
            onClick={() => {
              logAccountPageMenuItemTapped("Earnings");
              history.push(DeprecatedGlobalAppV1Paths.PAYROLL);
            }}
          >
            <ListItemIcon iconType="bank" />
            <ListItemText
              primary="Earnings"
              sx={{
                color: (theme) =>
                  shouldBeOnboardedInPaymentService ? theme.palette.error.main : undefined,
              }}
            />
          </ListItemButton>
        </ListItem>
        {isPayoutSettingsEnabled && (
          <ListItem divider>
            <ListItemButton
              onClick={() => {
                logAccountPageMenuItemTapped("Payout Settings");
                history.push(PAYOUT_SETTINGS_FULL_PATH);
              }}
            >
              <ListItemIcon iconType="calendar-clock" />
              <ListItemText primary="Payout Settings" />
            </ListItemButton>
          </ListItem>
        )}

        <ListItem divider>
          <ListItemButton
            onClick={() => {
              logAccountPageMenuItemTapped("Notification Preferences");
              history.push(NOTIFICATIONS_PREFERENCE_PATH_FULL);
            }}
          >
            <ListItemIcon iconType="bell-outlined" />
            <ListItemText primary="Notifications" />
          </ListItemButton>
        </ListItem>
        <ListItem divider>
          <ListItemButton
            onClick={() => {
              logAccountPageMenuItemTapped("Work with Friends");
              history.push(WORK_WITH_FRIENDS_FULL_PATH);
            }}
          >
            <ListItemIcon iconType="user-group" />
            <ListItemText primary="Work with Friends" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );
}
